import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SignOut, List, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { NAV_ITEMS, applyMenuOrder } from "@/lib/navItems";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [orderKeys, setOrderKeys] = useState([]);
  const [unread, setUnread] = useState({ email: 0, whatsapp: 0 });

  // Menü-Reihenfolge aus DB laden (falls admin sie angepasst hat)
  useEffect(() => {
    if (!user) return;
    api.get("/settings/menu-order").then(r => setOrderKeys(r.data.order || [])).catch(() => setOrderKeys([]));
    const onChange = (e) => setOrderKeys(e.detail || []);
    window.addEventListener("msb-menu-order-changed", onChange);
    return () => window.removeEventListener("msb-menu-order-changed", onChange);
  }, [user]);

  // Ungelesen-Counts alle 30 s pollen (nur für Admins)
  useEffect(() => {
    if (user?.role !== "admin") return;
    let stop = false;
    const load = () => api.get("/notifications/unread-counts")
      .then(r => { if (!stop) setUnread(r.data || { email: 0, whatsapp: 0 }); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    const onVis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const orderedItems = applyMenuOrder(NAV_ITEMS, orderKeys);
  const items = orderedItems.filter((i) => i.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        data-testid="app-sidebar"
        className={`${open ? "block" : "hidden"} md:block fixed md:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-border flex flex-col`}
      >
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-lg" style={{fontFamily:"'Work Sans',sans-serif"}}>M</span>
            </div>
            <div>
              <div className="font-semibold text-foreground leading-tight" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</div>
              <div className="text-xs text-muted-foreground">Cockpit</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((i) => {
            const count = i.badge ? (unread[i.badge] || 0) : 0;
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${i.to.replace("/", "") || "dashboard"}`}
                className={({ isActive }) => `msb-sidebar-item ${isActive ? "active" : ""}`}
              >
                <i.icon size={20} weight="duotone" />
                <span className="flex-1">{i.label}</span>
                {count > 0 && (
                  <span
                    data-testid={`nav-badge-${i.badge}`}
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold shadow-sm"
                    title={`${count} ungelesen`}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-white font-semibold">
              {user?.name?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-xl"
          >
            <SignOut size={16} weight="bold" />
            <span className="ml-2">Abmelden</span>
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen md:pl-0 min-w-0">
        <header className="md:hidden bg-white border-b border-border px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button data-testid="menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X size={24}/> : <List size={24}/>}
          </button>
          <div className="font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</div>
          <div className="w-6" />
        </header>

        <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
